/**
 * GPS — ZIYARET_TAKIP.md §5 + madde #776.
 *
 * Temsilcinin GÜN İZİ: android tarafının (madde #775) ziyarete başla/bitir ve güne başla/bitir
 * anında yazdığı GPS damgaları harita üzerinde sırayla çizilir.
 *
 * Neden ayrı sekme: Saha İzleme akışı ZAMAN eksenlidir, bu ekran KONUM eksenlidir. Aynı veriye
 * (visit_activities) iki farklı soru sorulur — "kaçta ne yaptı" ve "nerede dolaştı".
 *
 * Harita react-leaflet + OpenStreetMap; projede zaten kullanılan yığın (bkz. routePlan/RouteMap.jsx,
 * shipmentVehicle/ShipmentAreaMap.jsx). Ayrı bir harita servisi/anahtarı GEREKMEZ.
 */

import { useEffect, useMemo, useState } from 'react';
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { authHeaders } from '../listPageStyles';
import { fmtTime } from './shared';
import { IcRefresh } from './icons';

// Türkiye merkezli varsayılan görünüm — hiç noktası olmayan gün için.
const DEFAULT_CENTER = [39.0, 35.0];
const DEFAULT_ZOOM = 6;

/** Damga tipi → pin rengi. Giriş/çıkış ve gün uçları ayırt edilebilir olmalı. */
const POINT_TONES = {
  day_open: { color: '#0b2b5b', label: 'Güne Başlama' },
  day_close: { color: '#0b2b5b', label: 'Günü Bitirme' },
  gps_check_in: { color: '#16a34a', label: 'Check IN GPS' },
  gps_check_out: { color: '#dc2626', label: 'Check Out GPS' },
  visit_start: { color: '#16a34a', label: 'Ziyaret Başla' },
  visit_end: { color: '#dc2626', label: 'Ziyaret Bitir' },
};
const DEFAULT_TONE = { color: '#64748b', label: 'Hareket' };

const toneOf = (p) => POINT_TONES[p.activityType] || DEFAULT_TONE;

/** Gün uçları (açılış/kapanış) damla-pin ile, diğerleri daire ile çizilir. */
function endpointIcon(color, text) {
  return L.divIcon({
    className: 'vt-gps-pin',
    html: `<div style="
      width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);
      background:${color};border:2px solid #fff;box-shadow:0 2px 6px rgba(15,23,42,.35);
      display:flex;align-items:center;justify-content:center;">
      <span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:11px;">${text}</span>
    </div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });
}

/** Noktalar geldiğinde haritayı izin tamamını kapsayacak şekilde yerleştirir. */
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [40, 40] });
  }, [map, points]);
  return null;
}

/** Oturum seçici etiketi: "12.07.2026 · Halit Doğan · HB Gıda". */
function sessionLabel(d) {
  const day = d?.workDate || d?.startedAt;
  const dateStr = day ? new Date(day).toLocaleDateString('tr-TR') : '—';
  return [dateStr, d?.representativeName || 'Temsilci', d?.vendorName]
    .filter(Boolean).join(' · ');
}

export function GpsTab({ selectedDay, contextLabel, days }) {
  // #804 EKRAN FİLTRESİYLE KENDİLİĞİNDEN ÇİZER.
  //
  // Eskiden yalnız `selectedDay` (Kokpit'te bir satıra tıklanması) ile çalışıyordu: kullanıcı üstte
  // Distribütör + Temsilci + tarih aralığını verip "Listele" dediğinde bile harita
  // "Kokpit sekmesinden bir temsilci satırı seçin" diyordu — verdiği kriter göz ardı ediliyordu.
  //
  // Artık: Kokpit'ten seçim VARSA o kazanır (kullanıcının açık niyeti); yoksa filtrenin döndürdüğü
  // gün oturumlarının EN YENİSİ otomatik çizilir. Harita mimarisi gereği bir seferde BİR gün izi
  // çizilebildiği için (gps-track ucu oturum bazlı), diğer günlere sekme içindeki seçiciden
  // geçilir — böylece Kokpit'e gitmeden gün değiştirilebilir.
  const sessions = useMemo(() => (days?.items || []).filter((d) => d?.id), [days]);
  const [pickedId, setPickedId] = useState('');

  // Filtre değişince (yeni "Listele") elle yapılan seçim geçersizdir — sıfırlanır.
  useEffect(() => { setPickedId(''); }, [sessions]);

  const autoSession = sessions[0] || null;
  const activeSession = selectedDay
    || sessions.find((d) => String(d.id) === String(pickedId))
    || autoSession;
  const sessionId = activeSession?.id || '';
  // Kokpit seçimi yokken çizilen gün "otomatik seçilmiş"tir; kullanıcı bunu bilmeli.
  const autoPicked = !selectedDay && !pickedId && Boolean(autoSession);

  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) { setPoints(null); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(`/api/visit-tracking/gps-track/${sessionId}`, { headers: authHeaders() })
      .then(async (res) => {
        const body = await res.json().catch(() => null);
        if (!res.ok || (body?.success ?? body?.Success) === false) {
          throw new Error(body?.message || body?.Message || `İstek başarısız (${res.status}).`);
        }
        return body?.data ?? body?.Data ?? [];
      })
      .then((rows) => { if (!cancelled) setPoints(Array.isArray(rows) ? rows : []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  // Koordinatı olan damgalar, zaman sırasında. Sunucu sıralı döner; yine de garanti altına alınır.
  const track = useMemo(() => (points || [])
    .filter((p) => p.latitude != null && p.longitude != null)
    .map((p) => ({ ...p, lat: Number(p.latitude), lng: Number(p.longitude) }))
    .sort((a, b) => new Date(a.occurredAt) - new Date(b.occurredAt)), [points]);

  const totalKm = useMemo(() => {
    let sum = 0;
    for (let i = 1; i < track.length; i += 1) sum += haversineKm(track[i - 1], track[i]);
    return sum;
  }, [track]);

  // #804 Boş durum artık YALNIZCA filtrede hiç gün oturumu yoksa çıkar — "seçim yapmadın" değil,
  // "bu kriterde gün kaydı yok" der (gerçek sebep budur).
  if (!sessionId) {
    return (
      <div className="pane on">
        <div className="card" style={{ padding: 28, textAlign: 'center', color: 'var(--t3)', fontSize: 12.5 }}>
          Seçilen kriterde çizilecek gün kaydı yok.
          <div style={{ marginTop: 6, fontSize: 11.5 }}>
            Harita bir temsilcinin BİR GÜNÜNÜ çizer. Tarih aralığını genişletin ya da
            distribütör/temsilci seçimini değiştirip yeniden listeleyin.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pane on">
      {contextLabel && (
        <div className="alert info m" style={{ marginBottom: 12 }}><div>{contextLabel}</div></div>
      )}

      {/* #804 Gün seçici — Kokpit'e dönmeye gerek kalmadan filtre içindeki günler arasında geçiş. */}
      {!selectedDay && sessions.length > 0 && (
        <div className="card" style={{ padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label htmlFor="vt-gps-session" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            Çizilen Gün
          </label>
          <select
            id="vt-gps-session"
            value={String(sessionId)}
            onChange={(e) => setPickedId(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border-soft)', background: 'var(--input-bg)', color: 'var(--strong)', fontSize: 12.5, fontWeight: 600, minWidth: 260 }}
          >
            {sessions.map((d) => (
              <option key={d.id} value={String(d.id)}>{sessionLabel(d)}</option>
            ))}
          </select>
          <span style={{ fontSize: 11.5, color: 'var(--t3)' }}>
            {autoPicked
              ? `Filtredeki en yeni gün otomatik çizildi · toplam ${sessions.length} gün`
              : `Filtrede ${sessions.length} gün var`}
          </span>
        </div>
      )}

      <div className="card">
        <div className="sec-h" style={{ padding: '14px 16px 0', marginBottom: 10 }}>
          <div>
            <b>Gün İzi</b>
            <span className="sub" style={{ display: 'block' }}>
              Ziyaret giriş/çıkış ve gün açılış/kapanış GPS damgaları · zaman sırasında
            </span>
          </div>
          <div style={{ flex: 1 }} />
          <span className="gpill">{track.length} nokta</span>
          {track.length > 1 && (
            <span className="gpill" style={{ marginLeft: 6 }}>~{totalKm.toFixed(1)} km</span>
          )}
        </div>

        {loading && (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <IcRefresh size={14} /> Yükleniyor…
          </div>
        )}

        {error && (
          <div className="alert info m" style={{ margin: 16, borderLeftColor: 'var(--m-red)' }}>
            <div><b>Hata:</b> {error}</div>
          </div>
        )}

        {!loading && !error && track.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--t3)', fontSize: 12.5 }}>
            Bu gün için koordinatlı hareket yok.
            <div style={{ marginTop: 6, fontSize: 11.5 }}>
              GPS damgaları, mobil uygulamanın konum yazan sürümü sahaya çıktığı tarihten itibaren
              dolar; öncesindeki günlerde ziyaretlerin koordinatı kaydedilmemiştir.
            </div>
          </div>
        )}

        {!loading && !error && track.length > 0 && (
          <>
            <div style={{ height: 480, margin: '0 16px 12px', borderRadius: 12, overflow: 'hidden' }}>
              <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={track} />

                {/* İz çizgisi — damgalar arasındaki güzergah (gerçek yol değil, düz hat). */}
                <Polyline positions={track.map((p) => [p.lat, p.lng])} pathOptions={{ color: '#0073ea', weight: 3, opacity: 0.7, dashArray: '6 6' }} />

                {track.map((p, i) => {
                  const tone = toneOf(p);
                  const isEndpoint = p.activityType === 'day_open' || p.activityType === 'day_close';
                  const label = (
                    <Tooltip>
                      <div style={{ fontWeight: 800 }}>{fmtTime(p.occurredAt, true)} · {p.name || tone.label}</div>
                      {p.customerTitle && (
                        <div>{p.customerCode ? `${p.customerCode} — ` : ''}{p.customerTitle}</div>
                      )}
                      {p.gpsAccuracy != null && <div>Hassasiyet ~{Math.round(p.gpsAccuracy)} m</div>}
                    </Tooltip>
                  );
                  return isEndpoint ? (
                    <Marker key={p.id} position={[p.lat, p.lng]}
                      icon={endpointIcon(tone.color, p.activityType === 'day_open' ? 'B' : 'S')}>
                      {label}
                    </Marker>
                  ) : (
                    <CircleMarker key={p.id} center={[p.lat, p.lng]} radius={7}
                      pathOptions={{ color: '#fff', weight: 2, fillColor: tone.color, fillOpacity: 0.95 }}>
                      {label}
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>

            <div className="quickbar" style={{ borderTop: '1px solid var(--border-soft)', borderBottom: 'none' }}>
              <Legend color={POINT_TONES.day_open.color} text="Güne Başlama / Günü Bitirme (damla pin)" />
              <Legend color={POINT_TONES.gps_check_in.color} text="Check IN GPS" />
              <Legend color={POINT_TONES.gps_check_out.color} text="Check Out GPS" />
              <Legend color={DEFAULT_TONE.color} text="Diğer hareket" />
              <span style={{ marginLeft: 'auto', color: 'var(--t3)', fontSize: 11.5 }}>
                Kesikli çizgi damgalar arası düz hattır — gerçek güzergah değildir.
              </span>
            </div>

            {/* Damga listesi — haritada göz kaçırılanı okumak için. */}
            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              {track.map((p) => {
                const tone = toneOf(p);
                return (
                  <div className="arow" key={`row-${p.id}`}>
                    <span className="at">{fmtTime(p.occurredAt, true)}</span>
                    <span className="dot" style={{ color: tone.color }}>•</span>
                    <span className="nm">{p.name || tone.label}</span>
                    <span className="meta">
                      {p.customerTitle && <>{p.customerCode ? `${p.customerCode} · ` : ''}{p.customerTitle}</>}
                      {p.gpsAccuracy != null && <> · ~{Math.round(p.gpsAccuracy)} m</>}
                      {' · '}{p.lat.toFixed(5)}, {p.lng.toFixed(5)}
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Legend({ color, text }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--t2)' }}>
      <span style={{ width: 10, height: 10, borderRadius: 999, background: color, border: '1px solid #fff' }} />
      {text}
    </span>
  );
}

/** İki damga arası kuş uçuşu mesafe (km) — §4.6/4'teki haversine toplamının web karşılığı. */
function haversineKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export default GpsTab;
